import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { useFilter } from '../../../contexts/FilterContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import { useProjects, useClients, TransformedProject, Milestone } from './hooks/useProjects';
import { usePeople } from './hooks/usePeople';
import { useTimesheets } from './hooks/useTimesheets';
import { useMilestones } from './hooks/useMilestones';
import { Modal, Button, Card } from '../../../components/ui';
import ProjectList from './components/ProjectList';
import ProjectForm, { ProjectFormData } from './components/ProjectForm';
import ProjectDetails from './components/ProjectDetails';
import TimeTrackingView from './components/TimeTrackingView';
import { MilestoneModal } from './components/MilestoneModal';
import { ClientModal, ClientFormData } from './components/ClientModal';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { createClient } from '../../../services/api';

interface NewProjectState {
  name: string;
  description: string;
  infrastructureBudget: string;
  deliveryBudget: string;
  startDate: string;
  endDate: string;
  clientId: string;
}

const ProjectsPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedClientId, selectClient } = useClient();
  const { refreshFilterData } = useFilter();
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTimeTracking, setShowTimeTracking] = useState(false);

  const {
    projects,
    selectedProject,
    setSelectedProject,
    loading: projectsLoading,
    error,
    clientFilter,
    loadProjects,
    createProject,
    updateProjectStatus,
    exportProject,
    deleteProject,
    refreshProject
  } = useProjects(user!, selectedClientId);

  const {
    clients,
    loading: clientsLoading,
    loadClients
  } = useClients();

  const {
    people,
    loadPeople
  } = usePeople();

  const {
    timesheets,
    summary: timesheetSummary,
    loading: timesheetsLoading,
    loadTimesheets,
    addTimesheet,
    updateTimesheet,
    deleteTimesheet
  } = useTimesheets();

  const {
    loading: milestonesLoading,
    createMilestone,
    updateMilestone,
    deleteMilestone
  } = useMilestones();

  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);

  const [showClientModal, setShowClientModal] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newProject, setNewProject] = useState<NewProjectState>({
    name: '',
    description: '',
    infrastructureBudget: '',
    deliveryBudget: '',
    startDate: '',
    endDate: '',
    clientId: ''
  });

  const canCreateProjects = useMemo(() => {
    return hasPermission(PERMISSIONS.PROJECTS_CREATE);
  }, [hasPermission]);

  const handleSelectProject = useCallback((project: TransformedProject | null) => {
    setSelectedProject(project);
    if (project) {
      const params = new URLSearchParams(searchParams);
      params.set('project', project.id);
      if (selectedClientId) {
        params.set('client', selectedClientId);
      }
      setSearchParams(params, { replace: true });
    } else {
      const params = new URLSearchParams(searchParams);
      params.delete('project');
      setSearchParams(params, { replace: true });
    }
  }, [selectedClientId, searchParams, setSearchParams, setSelectedProject]);

  useEffect(() => {
    if (canCreateProjects) {
      loadClients();
    }
  }, [canCreateProjects, loadClients]);

  const hasHandledClientRef = useRef(false);
  useEffect(() => {
    const clientParam = searchParams.get('client');
    if (clientParam && clientParam !== selectedClientId && !hasHandledClientRef.current) {
      hasHandledClientRef.current = true;
      selectClient(clientParam);
    }
  }, [searchParams, selectedClientId, selectClient]);

  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam && projects.length > 0) {
      const project = projects.find(p => p.id === projectParam);
      if (project) {
        setSelectedProject(prev => {
          if (prev?.id === projectParam) return prev;
          return project;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, projects]);

  useEffect(() => {
    if (selectedProject && !showTimeTracking) {
      refreshProject(selectedProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, showTimeTracking]);

  useEffect(() => {
    if (showTimeTracking) {
      if (projects.length > 0) {
        projects.forEach(project => {
          loadPeople(project.id);
        });
      }
      loadTimesheets({ projects, filters: {} });
    } else if (selectedProject) {
      loadPeople(selectedProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTimeTracking, selectedProject?.id, projects.length]);

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
    if (hasPermission(PERMISSIONS.CLIENTS_VIEW) || hasPermission(PERMISSIONS.CLIENTS_MANAGE)) {
      loadClients();
    }
  };

  const handleCreateProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const userClientId = user?.role === 'client' ? (user.client_id ?? '') : '';
    const resolvedClientId = (userClientId || selectedClientId || newProject.clientId || '').trim();
    if (!resolvedClientId) {
      alert('Please select a client before creating a project.');
      return;
    }

    setCreateLoading(true);

    try {
      const projectData = {
        ...newProject,
        infrastructureBudget: Number.parseFloat(newProject.infrastructureBudget) || 0,
        deliveryBudget: Number.parseFloat(newProject.deliveryBudget) || 0,
        clientId: resolvedClientId || undefined
      };

      const result = await createProject(projectData);
      if (result.success) {
        refreshFilterData();
        setShowCreateModal(false);
        setNewProject({
          name: '',
          description: '',
          infrastructureBudget: '',
          deliveryBudget: '',
          startDate: '',
          endDate: '',
          clientId: ''
        });
      } else {
        alert('Failed to create project: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error creating project:', err);
      alert('Failed to create project');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedProject) return;

    try {
      const result = await exportProject(selectedProject.id);
      if (result.success) {
        alert('Project exported successfully');
      } else {
        alert('Failed to export project: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error exporting project:', err);
      alert('Failed to export project');
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedProject) return;

    try {
      const result = await updateProjectStatus(selectedProject.id, newStatus);
      if (result.success) {
        alert('Project status updated successfully');
      } else {
        alert('Failed to update project status: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error updating project status:', err);
      alert('Failed to update project status');
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    if (!globalThis.confirm(`Are you sure you want to delete the project "${selectedProject.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await deleteProject(selectedProject.id);
      if (result.success) {
        refreshFilterData();
        alert('Project deleted successfully');
      } else {
        alert('Failed to delete project: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    }
  };

  const handleAddMilestone = () => {
    setEditingMilestone(null);
    setShowMilestoneModal(true);
  };

  const handleEditMilestone = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setShowMilestoneModal(true);
  };

  const handleMilestoneSubmit = async (milestoneData: { name: string; description: string; start_date: string; end_date: string; status: string }) => {
    if (!selectedProject) return;

    try {
      let result: { success: boolean; error?: string };
      if (editingMilestone) {
        result = await updateMilestone(selectedProject.id, editingMilestone.id, milestoneData);
      } else {
        result = await createMilestone(selectedProject.id, milestoneData);
      }

      if (result.success) {
        const action = editingMilestone ? 'updated' : 'added';
        alert(`Milestone ${action} successfully`);
        setShowMilestoneModal(false);
        setEditingMilestone(null);
        await loadProjects();
      } else {
        const action = editingMilestone ? 'update' : 'add';
        alert(`Failed to ${action} milestone: ${result.error}`);
      }
    } catch (err: unknown) {
      console.error('Error with milestone:', err);
      const action = editingMilestone ? 'update' : 'add';
      alert(`Failed to ${action} milestone`);
    }
  };

  const handleDeleteMilestone = async (milestone: Milestone) => {
    if (!selectedProject) return;

    if (!globalThis.confirm(`Are you sure you want to delete the milestone "${milestone.name}"?`)) {
      return;
    }

    try {
      const result = await deleteMilestone(selectedProject.id, milestone.id);
      if (result.success) {
        alert('Milestone deleted successfully');
        await loadProjects();
      } else {
        alert('Failed to delete milestone: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error deleting milestone:', err);
      alert('Failed to delete milestone');
    }
  };

  const handleCreateClient = () => {
    setShowClientModal(true);
  };

  const handleClientSubmit = async (clientData: ClientFormData): Promise<boolean> => {
    setClientLoading(true);
    try {
      const result = await createClient(clientData);
      if (result.success) {
        alert('Client created successfully');
        setShowClientModal(false);
        await loadClients();
        return true;
      } else {
        alert('Failed to create client: ' + result.error);
        return false;
      }
    } catch (err: unknown) {
      console.error('Error creating client:', err);
      alert('Failed to create client');
      return false;
    } finally {
      setClientLoading(false);
    }
  };

  const handleTimeTrackingFilterChange = (filters: { startDate: string; endDate: string; employeeId: string }) => {
    loadTimesheets({
      selectedProject,
      projects: selectedProject ? [selectedProject] : projects,
      filters
    });
  };

  const handleTimeTrackingProjectChange = (projectId: string | null) => {
    if (projectId) {
      const project = projects.find(p => p.id === projectId) ?? null;
      handleSelectProject(project);
    } else {
      handleSelectProject(null);
    }
  };

  const handleAddTimesheet = async (timesheetData: Record<string, unknown>) => {
    const result = await addTimesheet({
      ...timesheetData,
      projectId: selectedProject?.id ?? projects[0]?.id
    });

    if (result.success) {
      loadTimesheets({ projects: selectedProject ? [selectedProject] : projects, filters: {} });
    } else {
      alert('Failed to add timesheet: ' + result.error);
    }
  };

  const handleEditTimesheet = async (timesheetId: string, timesheetData: Record<string, unknown>) => {
    const result = await updateTimesheet(timesheetId, {
      ...timesheetData,
      projectId: selectedProject?.id ?? projects[0]?.id
    });

    if (result.success) {
      loadTimesheets({ projects: selectedProject ? [selectedProject] : projects, filters: {} });
    } else {
      alert('Failed to update timesheet: ' + result.error);
    }
  };

  const handleDeleteTimesheet = async (timesheetId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this timesheet entry?')) {
      return;
    }

    const projectId = selectedProject?.id ?? projects[0]?.id;
    const result = await deleteTimesheet(timesheetId, projectId);

    if (result.success) {
      loadTimesheets({ projects: selectedProject ? [selectedProject] : projects, filters: {} });
    } else {
      alert('Failed to delete timesheet: ' + result.error);
    }
  };

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
              {showTimeTracking ? 'Time Tracking' : 'Projects'}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
              {showTimeTracking
                ? 'View and manage timesheet entries for all employees across projects'
                : 'Manage and monitor your projects'}
            </p>
          </div>
          <div className="flex gap-3">
            {showTimeTracking ? (
              <Button variant="secondary" onClick={() => setShowTimeTracking(false)}>
                Back to Projects
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setShowTimeTracking(true)}>
                Time Tracking
              </Button>
            )}
            {canCreateProjects && (
              <Button onClick={handleShowCreateModal}>
                Create New Project
              </Button>
            )}
          </div>
        </div>

        {/* Time Tracking View */}
        {showTimeTracking ? (
          <TimeTrackingView
            projects={projects}
            people={people}
            timesheets={timesheets}
            summary={timesheetSummary}
            loading={timesheetsLoading}
            selectedProject={selectedProject}
            onProjectFilterChange={handleTimeTrackingProjectChange}
            onFilterChange={handleTimeTrackingFilterChange}
            onAddTimesheet={handleAddTimesheet}
            onEditTimesheet={handleEditTimesheet}
            onDeleteTimesheet={handleDeleteTimesheet}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Project List */}
              <div className="lg:col-span-1">
                <Card padding="p-0">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Project List
                    </h3>
                  </div>

                  {projects.length === 0 ? (
                    <div className="px-6 py-8 text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        No projects found
                      </p>
                      {canCreateProjects && (
                        <Button
                          onClick={handleShowCreateModal}
                          variant="ghost"
                          className="mt-4"
                        >
                          Create your first project
                        </Button>
                      )}
                    </div>
                  ) : (
                    <ProjectList
                      projects={projects}
                      selectedProject={selectedProject}
                      onSelectProject={handleSelectProject}
                      clientFilter={clientFilter}
                    />
                  )}
                </Card>
              </div>

              {/* Project Details */}
              <div className="lg:col-span-4">
                {selectedProject ? (
                  <ProjectDetails
                    project={selectedProject}
                    onUpdateStatus={handleUpdateStatus}
                    onAddMilestone={handleAddMilestone}
                    onEditMilestone={handleEditMilestone}
                    onDeleteMilestone={handleDeleteMilestone}
                    onExport={handleExport}
                    onDelete={handleDeleteProject}
                    onRefresh={() => refreshProject(selectedProject.id)}
                  />
                ) : (
                  <Card>
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                        No project selected
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Select a project from the list to view its details
                      </p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
      >
        <ProjectForm
          formData={newProject as unknown as ProjectFormData}
          onChange={setNewProject as unknown as (data: ProjectFormData) => void}
          onSubmit={handleCreateProject}
          onCancel={() => setShowCreateModal(false)}
          clients={clients}
          clientsLoading={clientsLoading}
          loading={createLoading}
          mode="create"
          onCreateClient={handleCreateClient}
          clientSelectionMode={
            user?.role === 'partner' || user?.role === 'client' ? 'hidden' : 'required'
          }
        />
      </Modal>

      {/* Milestone Modal */}
      <MilestoneModal
        isOpen={showMilestoneModal}
        onClose={() => {
          setShowMilestoneModal(false);
          setEditingMilestone(null);
        }}
        onSubmit={handleMilestoneSubmit}
        milestone={editingMilestone}
        loading={milestonesLoading}
      />

      {/* Client Modal */}
      <ClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        onSubmit={handleClientSubmit}
        loading={clientLoading}
      />
    </div>
  );
};

export default ProjectsPage;
